import { useState } from "react";
import CallSummaryCell from "../calls/CallSummaryCell";
import RecordingButton from "../calls/RecordingButton";
import Badge from "../common/Badge";
import EmptyState from "../common/EmptyState";
import SearchInput from "../common/SearchInput";
import Skeleton from "../common/Skeleton";
import { CALL_STATUS_FILTERS, PAGE_SIZE } from "../../utils/constants";
import {
  getCallDirectionMeta,
  getCallStatusMeta,
} from "../../utils/helpers";
import { formatDateTime, formatDuration, formatPhone } from "../../utils/formatters";
import { FiEye } from "react-icons/fi";

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}

export default function CallsTable({
  calls,
  total,
  currentPage,
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onPrevPage,
  onNextPage,
  onOpenTranscript,
  onDownloadTranscript,
  loading,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const hasFilters = Boolean(searchValue.trim() || statusFilter);
  const hasGroupedCalls = calls.some((call) => Array.isArray(call.callHistory));
  const currentRowsLabel = hasGroupedCalls
    ? `${calls.length} group${calls.length === 1 ? "" : "s"} shown`
    : `${calls.length} row${calls.length === 1 ? "" : "s"} shown`;

  const getCurrentFilterLabel = () => {
    const currentFilter = CALL_STATUS_FILTERS.find(filter => filter.value === statusFilter);
    return currentFilter ? currentFilter.label : "All Status";
  };

  const handleFilterSelect = (value) => {
    onStatusFilterChange(value);
    setIsDropdownOpen(false);
  };

  // Generate page numbers to display (with ellipsis for large page counts)
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const currentPageNum = currentPage + 1;
      if (currentPageNum <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPageNum >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const goToPage = (page) => {
    const pageIndex = page - 1;
    if (pageIndex < currentPage) {
      onPrevPage();
    } else if (pageIndex > currentPage) {
      onNextPage();
    }
  };

  return (
    <div className="calls-section">
      <div className="section-row">
        <div className="section-title section-title-no-margin">Recent Calls</div>
        <div className="calls-count">
          {loading
            ? "Loading..."
            : `Showing ${calls.length} of ${total} ${
                hasGroupedCalls ? "phone groups" : "calls"
              }`}
        </div>
      </div>

      <div className="filters-bar">
        <SearchInput
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search phone, name, or summary..."
        />
        
        {/* Dropdown for filters */}
        <div className="filter-dropdown">
          <button 
            className="filter-dropdown-trigger"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span>{getCurrentFilterLabel()}</span>
            <ChevronDownIcon />
          </button>
          
          {isDropdownOpen && (
            <div className="filter-dropdown-menu">
              {CALL_STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value || "all"}
                  className={`filter-dropdown-item ${statusFilter === filter.value ? "active" : ""}`}
                  onClick={() => handleFilterSelect(filter.value)}
                >
                  {filter.label}
                  {statusFilter === filter.value && <span className="check-mark">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {/* <th className="th-check">
                <input
                  type="checkbox"
                  className="row-check"
                  checked={allSelected}
                  onChange={(event) => onToggleAll(event.target.checked)}
                  title="Select all"
                />
              </th> */}
              <th>Time</th>
              <th>Dir</th>
              <th>Phone</th>
              <th>Caller</th>
              <th>Duration</th>
              {/* <th>Summary</th> */}
              <th>Status</th>
              <th title="Call recording (mirrored from PBX to S3)">Audio</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10">
                  <div className="empty-state">
                    <Skeleton style={{ height: 14, width: 180, margin: "0 auto 8px" }} />
                    <Skeleton style={{ height: 10, width: 120, margin: "0 auto" }} />
                  </div>
                </td>
              </tr>
            ) : !calls.length ? (
              <tr>
                <td colSpan="10">
                  <EmptyState
                    icon="🟢"
                    title={hasFilters ? "No calls match your filters" : "No calls yet"}
                    subtitle={
                      hasFilters
                        ? "Try adjusting search or filters."
                        : "Recent call activity will appear here when data is available."
                    }
                  />
                </td>
              </tr>
            ) : (
              calls.map((call) => {
                const callStatus = getCallStatusMeta(call);
                const direction = getCallDirectionMeta(call);

                return (
                  <tr
                    style={{ cursor: "pointer" }}
                    key={call.__groupKey || call.__key}
                  >
                    {/* <td className="td-check" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="row-check"
                        checked={selected}
                        onChange={(event) =>
                          onToggleId(call.__key, event.target.checked)
                        }
                      />
                    </td> */}
                    <td className="td-time" onClick={() => onOpenTranscript(call)}>
                      {formatDateTime(call.created_at)}
                    </td>
                    <td className="td-dir" onClick={() => onOpenTranscript(call)}>
                      <Badge variant={direction.variant}>
                        {direction.icon} {direction.label}
                      </Badge>
                    </td>
                    <td className="td-phone" onClick={() => onOpenTranscript(call)}>
                      {formatPhone(call.phone_number)}
                    </td>
                    <td className="td-caller" onClick={() => onOpenTranscript(call)}>
                      {call.caller_name || <span className="muted-text">Unknown</span>}
                      {!call.transcript ? (
                        <Badge variant="no-tx" title="No transcript">
                          ⚠
                        </Badge>
                      ) : null}
                    </td>
                    <td className="td-duration" onClick={() => onOpenTranscript(call)}>
                      {formatDuration(call.duration_seconds)}
                    </td>
                    {/* <td className="td-summary" onClick={() => onOpenTranscript(call)}>
                      <CallSummaryCell
                        call={call}
                        onViewCall={onOpenTranscript}
                      />
                    </td> */}
                    <td className="td-status" onClick={() => onOpenTranscript(call)}>
                      <Badge variant={callStatus.variant}>
                        {callStatus.icon} {callStatus.label}
                      </Badge>
                    </td>
                    <td className="td-recording" onClick={(event) => event.stopPropagation()}>
                      <RecordingButton call={call} />
                    </td>
                    <td className="td-actions" onClick={(event) => event.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title={
                            call.groupedByPhone
                              ? "View follow-up history"
                              : "View summary details"
                          }
                          aria-label={
                            call.groupedByPhone
                              ? `View follow-up history for ${call.caller_name || call.phone_number || "call"}`
                              : `View call details for ${call.caller_name || call.phone_number || "call"}`
                          }
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                          onClick={() => onOpenTranscript(call)}
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="calls-pagination">
            <div className="pagination-info">
              <span className="pagination-range">
                Page {currentPage + 1} of {totalPages}
              </span>
              <span className="pagination-meta">{currentRowsLabel}</span>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                type="button"
                onClick={onPrevPage}
                disabled={currentPage === 0}
              >
                <ChevronLeftIcon />
                Previous
              </button>
              
              <div className="pagination-pages">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`pagination-page ${currentPage + 1 === page ? "active" : ""}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>
              
              <button
                className="pagination-btn"
                type="button"
                onClick={onNextPage}
                disabled={currentPage >= totalPages - 1}
              >
                Next
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
